import { Link, Typography } from "@material-ui/core";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { AutocompleteOptions, SelectOptions } from "../../../assets/data/options";
import { Company } from "../../../backend/generated/company-model";
import { Experience } from "../../../backend/generated/experience-model";
import { PaymentMethod } from "../../../backend/generated/payment-method-model";
import { UpdateExperienceInput } from "../../../backend/generated/update-experience-input";
import { UserUpdatability } from "../../../backend/generated/update-user-input";
import { User } from "../../../backend/generated/user-model";
import { isUpdatable } from "../../../backend/types/updatability";
import { InvestmentClass } from "../../../backend/types/utils";
import { StoreState } from "../../../store/types";
import { UserInputTypeUnion, UserManageable, UserType } from "../../../store/user/types";
import { getFormatter, InputType } from "../../../utils/inputs";
import { SubformProps } from "../../SubformProps";
import { InputFieldPropsUnion } from "../elements/types";
import { CompanyField, CompanyInputTypeKey } from "./CompanyField";
import { ExperienceField, ExperienceInputTypeIndexkey, ExperienceInputTypeKey, ExperienceInputTypeSubkey } from "./ExperienceField";
import { AutocompleteInternal, CheckboxInternal, CountryAutocompleteInternal, DatePickerInternal, KeyUndefinedError, PhoneFieldInternal, SelectFieldInternal, TextFieldInternal } from "./InputField";

type UserFieldDescriptor = {
  [key in keyof InputFieldPropsUnion]: any;
}

type Predefined =
  | 'accountType' // TODO(Marko): Should be handled as others and removed from this exception list
  | 'document'
  | 'id'
  | 'wallet'

export type UserInputTypeKey = keyof Omit<UserInputTypeUnion, Predefined>;

type UserInputTypeSubkey =
  | CompanyInputTypeKey
  | ExperienceInputTypeSubkey

type UserInputTypeIndexkey =
  | ExperienceInputTypeIndexkey

type UserFieldProps = SubformProps<UserType> & Partial<UserFieldDescriptor> & {
  id: UserInputTypeKey;
  indexkey?: UserInputTypeIndexkey;
  noLabels?: boolean;
  subkey?: UserInputTypeSubkey;
}

export const UserField = (props: UserFieldProps) : JSX.Element | null => {
  // Subform Props

  const {
    errors,
    indexkey,
    onClick,
    setFieldValue,
    setValues,
    subkey,
    touched,
    values,
  } = props;

  // Generated Props

  const id = props.id as UserInputTypeKey;

  const disabled = !isUpdatable(
    id,
    new User(values),
    UserUpdatability
  );

  // State

  const [storedExperience, setStoredExperience] = useState<Experience & UpdateExperienceInput | undefined>(undefined);

  // Store

  const storageUserDetails = useSelector<StoreState, UserManageable>((state) => state.currentUser);

  // Hooks

  const { t } = useTranslation();

  // Building Component

  switch (id) {
    case "address": {
      const text = t("Address");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "addressCity": {
      const text = t("City of residence");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "addressCountry": {
      const text = t("Address country");
      return (
        <CountryAutocompleteInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "addressZipcode": {
      const text = t("Zipcode");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
	  case "birthCity": {
      const text = t("Birth city");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
	  case "birthDate": {
      let initialFocusedDate = new Date();
      initialFocusedDate.setFullYear(initialFocusedDate.getFullYear() - 50);

      let maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() - 18);

      return (
        <DatePickerInternal
          initialFocusedDate={initialFocusedDate}
          label={t("Birth date")}
          maxDate={maxDate}
          disabled={disabled}
          { ...props }
        />
      );
    }
	  case "cellphone": {
      return (
        <PhoneFieldInternal
          label={t("Cellphone number")}
          placeholder={t("+49 12 345678")}
          disabled={disabled}
          { ...props }
        />
      );
    }
	  case "citizenship": {
      const text = t("Citizenship");
      return (
        <CountryAutocompleteInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "company": {
      if (subkey !== undefined && 'company' in values) {
        return (
          <CompanyField
            errors={errors.company || {}}
            id={subkey as CompanyInputTypeKey}
            setFieldValue={(
              id: string, value: any
            ) => {
              let field = { ...values.company };
              field[id] = value;
              setFieldValue(
                'company',
                field
              );
            }}
            setValues={(values: any) => setFieldValue(
              'company',
              values
            )}
            touched={touched.company || {}}
            values={'company' in values && values.company || new Company()}
          />
        );
      } else {
        throw new Error('Accessing company but no company found');
      }
    }
    case "confirmPassword": {
      const text = t("Confirm password");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          type="password"
          { ...props }
        />
      );
    }
    case "confirmWalletPassword": {
      const text = t("Please enter your wallet password again");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          type="password"
          { ...props }
        />
      );
    }
    case "customerInfoEffectaGmbh": {
      return (
        <CheckboxInternal
          label={
            <Typography color="textSecondary">
              {t("TERMS_CUSTOMER_INFO_PART_1")}
              <Link className="text--link" onClick={onClick}>
                {t("TERMS_CUSTOMER_INFO_LINK")}
              </Link>
              {t("TERMS_CUSTOMER_INFO_PART_2")}
            </Typography>
          }
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "defaultPaymentMethod": {
      return (
        <SelectFieldInternal
          label={t("Default payment method")}
          placeholder={t("Select...")}
          options={'paymentMethods' in values && values.paymentMethods?.map((account: PaymentMethod) => ({
            label: account.IBAN || "",
            value: account.id
          })) || []}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "email": {
      return (
        <TextFieldInternal
          label={t("Email")}
          placeholder={t("email@mail.de")}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "employment": {
      const text = t("Current occupation");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "experience": {
      if (subkey !== undefined && indexkey !== undefined && 'experience' in values) {
        const midkey = Object.values(InvestmentClass).includes(indexkey as InvestmentClass) ? "skills" : "activities";
        return (
          <ExperienceField
            errors={errors.experience || {}}
            id={midkey as ExperienceInputTypeKey}
            subkey={subkey as ExperienceInputTypeSubkey}
            indexkey={indexkey as ExperienceInputTypeIndexkey}
            setFieldValue={(
              id: string, value: any
            ) => {
              let field = { ...values.experience };
              field[id] = value;
              setFieldValue(
                'experience',
                field
              );
            }}
            setValues={(values: any) => setFieldValue(
              'experience',
              values
            )}
            touched={touched.experience || {}}
            values={'experience' in values && values.experience || new Experience()}
            disabled={disabled || 'notProvidingExperience' in values && values.notProvidingExperience}
          />
        );
      } else {
        throw new Error('Accessing experience but no experience found');
      }
    }
    case "firstName": {
      const text = t("First name");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "gender": {
      return (
        <SelectFieldInternal
          label={t("Gender")}
          placeholder={t("Select...")}
          options={SelectOptions().gender}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "highestEducation": {
      const text = t("Highest education");
      return (
        <AutocompleteInternal
          label={text}
          placeholder={text}
          options={AutocompleteOptions().educationLevel}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "idDocumentCountry": {
      const text = t("ID document country");
      return (
        <CountryAutocompleteInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "idDocumentExpiryDate": {
      let initialFocusedDate = new Date();
      initialFocusedDate.setFullYear(initialFocusedDate.getFullYear() + 5);

      let minDate = new Date();
      minDate.setDate(minDate.getDate() + 1);

      return (
        <DatePickerInternal
          initialFocusedDate={initialFocusedDate}
          label={t("ID document expiry date")}
          minDate={minDate}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "idDocumentIssueDate": {
      let initialFocusedDate = new Date();
      initialFocusedDate.setFullYear(initialFocusedDate.getFullYear() - 5);

      let maxDate = new Date();

      return (
        <DatePickerInternal
          initialFocusedDate={initialFocusedDate}
          label={t("ID document issue date")}
          maxDate={maxDate}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "idDocumentIssuingAgency": {
      const text = t("Issuing agency");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "idDocumentNumber": {
      const text = t("ID document number");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "idDocumentType": {
      return (
        <SelectFieldInternal
          label={t("ID type")}
          placeholder={t("Select...")}
          options={SelectOptions().idDocumentType}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "lastName": {
      const text = t("Last name");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "notProvidingExperience": {
      return (
        <CheckboxInternal
          label={t("I don't want to give any details")}
          onChange={(e: any) => {
            const format = getFormatter(InputType.Checkbox);
            const value = format(e);

            let newExperience = null;
            if (value) {
              setStoredExperience('experience' in values && values.experience || undefined);
            } else {
              newExperience = storedExperience;
            }

            setValues({
              ...values,
              notProvidingExperience: value,
              experience: newExperience,
            })
          }}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "notUSTax": {
      return (
        <CheckboxInternal
          label={t("I am not a US tax resident")}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "password": {
      const text = t("Password");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          type="password"
          { ...props }
        />
      );
    }
    case "politicallyExposed": {
      return (
        <SelectFieldInternal
          label={t("Are you a politically exponated person?")}
          placeholder={t("Select...")}
          options={SelectOptions().yesNo}
          onChange={(e: any) => {
            const format = getFormatter(InputType.TextField);
            const value = format(e);
            setValues({
              ...values,
              politicallyExposed: value,
              politicallyExposedReason: undefined,
            })
          }}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "politicallyExposedReason": {
      const text = t("Reason for being politically exposed");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "privacyPolicy": {
      return (
        <CheckboxInternal
          label={
            <Typography color="textSecondary">
              {t("TERMS_PRIVACY_POLICY_PART_1")}
              <Link className="text--link" onClick={onClick}>
                {t("TERMS_PRIVACY_POLICY_LINK")}
              </Link>
              {t("TERMS_PRIVACY_POLICY_PART_2")}
            </Typography>
          }
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "skipIdNow": {
      return (
        <CheckboxInternal
          label={t("Skip IDNow")}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "subscribedToProductUpdates": {
      return (
        <CheckboxInternal
          label={t("I hereby consent to product updates")}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "termsEffectaGmbh": {
      return (
        <CheckboxInternal
          label={
            <Typography color="textSecondary">
              {t("TERMS_EFFECTA_AGB_PART_1")}
              <Link className="text--link" onClick={onClick}>
                {t("TERMS_EFFECTA_AGB_LINK")}
              </Link>
              {t("TERMS_EFFECTA_AGB_PART_2")}
            </Typography>
          }
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "termsFinproAG": {
      return (
        <CheckboxInternal
          label={
            <Typography color="textSecondary">
              {t("TERMS_FINPRO_AGB_PART_1")}
              <Link className="text--link" onClick={onClick}>
                {t("TERMS_FINPRO_AGB_LINK")}
              </Link>
              {t("TERMS_FINPRO_AGB_PART_2")}
            </Typography>
          }
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "taxNumber": {
      const text = t("Tax number");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          { ...props }
        />
      );
    }
    case "walletPassword": {
      const text = storageUserDetails.wallet ? t("Please enter your wallet password") : t("Please set your wallet password");
      return (
        <TextFieldInternal
          label={text}
          placeholder={text}
          disabled={disabled}
          type="password"
          { ...props }
        />
      );
    }
    default: {
      KeyUndefinedError(id); // Compile-time check for unhandled cases
    }
  }

  return <></>;
}
